/**
 * Référentiel des entreprises adhérentes — le poste de la chargée de
 * clientèle (CCT § 6 : « BDD client, statuts adhérents »).
 *
 * Les champs suivent la fiche de souscription papier d'AFS : identification
 * de la société, contact RH, puis le contrat souscrit pour une offre —
 * effectifs, masse salariale, cotisation et sa répartition, durée, date
 * d'effet.
 */
import { z } from 'zod';
import { AGE_LIMITE_ENFANT_MAX, AGE_LIMITE_ENFANT_MIN, STATUTS_ADHERENT, } from '../rules/eligibilite.js';
import { ORDRES_DE_TRI, schemaPage, schemaPagination } from './administration.js';
import { ETATS_CONFIRMATION, STATUTS_PERSONNE, schemaFichierPiece } from './personnes.js';
import { STATUTS_IMPORT_DNS } from './dns.js';
import { POPULATIONS_COUVERTES, PRECISION_POPULATION_MAX, refusPopulationCouverte, } from '../rules/population-couverte.js';
const texteCourt = (max) => z.string().trim().max(max);
const facultatif = (max) => texteCourt(max).optional().nullable();
/** `AAAA-MM-JJ`. */
const dateCivile = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ');
const montant = z.number().min(0).max(1_000_000_000_000);
const pourcentage = z.number().min(0).max(100);
/**
 * Une adresse e-mail à l'ÉCRITURE : création, modification, reprise de la
 * liste. La lecture ne la revérifie pas (`schemaEntrepriseDetaillee`) : une
 * adresse déjà en base, même fausse, ne doit pas faire tomber la fiche.
 */
const schemaEmail = z.string().trim().check(z.email()).max(160);
/** La même règle que l'écriture, pour qui doit trier sans refuser (reprise, écran). */
export function estAdresseEmail(valeur) {
    return schemaEmail.safeParse(valeur).success;
}
/** Au féminin : c'est une entreprise. Le libellé se lit sans la couleur. */
export const LIBELLE_STATUT_ADHERENT = {
    ACTIF: 'Active',
    EN_RETARD: 'En retard de paiement',
    SUSPENDU: 'Suspendue',
    RESILIATION_EN_PREAVIS: 'Résiliation en préavis',
    RESILIE: 'Résiliée',
};
// ─────────────────────────────────────────────────────────────────────
//  Liste
// ─────────────────────────────────────────────────────────────────────
/**
 * CP-07 — l'entité d'affiliation : AFS, ou une entité partenaire (FUNTOA,
 * FUNABE…) dont les affiliés sont reçus dans les centres AFS. Le code est la
 * clé ; le nom est le libellé, qui se corrige.
 */
export const schemaEntiteAffiliation = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
});
/** Le code de l'entité AFS : la première proposée, celle d'une reprise sans entité. */
export const CODE_ENTITE_AFS = 'AFS';
export const TRIS_ENTREPRISES = ['nom', 'code', 'nif', 'salaries', 'dernierDns', 'statut'];
export const schemaFiltresEntreprises = schemaPagination.extend({
    /** Code, raison sociale, NIF ou alias. */
    recherche: z.string().trim().max(120).optional(),
    statut: z.enum(STATUTS_ADHERENT).optional(),
    /** CP-07 — le code de l'entité d'affiliation, sans égard à la casse. */
    entite: z.string().trim().min(1).max(20).optional(),
    /** Seulement celles sans DNS depuis plus que le délai d'alerte (RG-PA-06). */
    dnsObsolete: z.coerce.boolean().optional(),
    tri: z.enum(TRIS_ENTREPRISES).default('nom'),
    ordre: z.enum(ORDRES_DE_TRI).default('asc'),
});
export const schemaEntrepriseListee = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    nif: z.string().nullable(),
    entiteAffiliation: schemaEntiteAffiliation,
    statut: z.enum(STATUTS_ADHERENT),
    aConfirmer: z.boolean(),
    /** Titulaires actifs, tous contrats confondus. Les ayants droit ne comptent pas. */
    salariesActifs: z.number().int(),
    dernierDnsRecuLe: z.string().nullable(),
    /** Jours écoulés depuis le dernier DNS, `null` s'il n'y en a jamais eu. */
    joursSansDns: z.number().int().nullable(),
    /** RG-PA-06 — au-delà du délai d'alerte, ou jamais reçu. */
    dnsObsolete: z.boolean(),
});
export const schemaPageEntreprises = schemaPage(schemaEntrepriseListee).extend({
    /** Le délai d'alerte appliqué, en jours — pour que l'écran l'écrive. */
    delaiAlerteJours: z.number().int(),
    /** Combien d'entreprises sont en alerte, toutes pages confondues. */
    nbDnsObsoletes: z.number().int(),
});
// ─────────────────────────────────────────────────────────────────────
//  Fiche
// ─────────────────────────────────────────────────────────────────────
const schemaIdentification = z.object({
    nom: texteCourt(200).min(2, 'Raison sociale requise.'),
    formeJuridique: facultatif(80),
    nif: facultatif(30),
    numeroStatistique: facultatif(40),
    rcs: facultatif(60),
    adresse: facultatif(300),
    ville: facultatif(120),
    codePostal: facultatif(20),
    pays: facultatif(80),
    /**
     * Du texte libre, pas un numéro : la liste officielle d'AFS y met
     * plusieurs numéros avec le nom et le service de chacun (jusqu'à
     * 102 caractères relevés le 23/09/2026). Plus court, la fiche reprise
     * ne s'ouvrirait plus — ce schéma valide aussi la réponse.
     */
    telephone: facultatif(200),
    email: schemaEmail.optional().nullable(),
    emailSecondaire: schemaEmail.optional().nullable(),
    secteurActivite: facultatif(120),
    /** La personne qui transmet le DNS, et qu'on relance à 60 jours (RG-PA-06). */
    contactRhNom: facultatif(160),
    contactRhTelephone: facultatif(200),
    contactRhEmail: schemaEmail.optional().nullable(),
});
/** Un contrat tel que la fiche de souscription le décrit, pour une offre. */
export const schemaSouscription = z.object({
    offreId: z.uuid(),
    /** « cadres », « non-cadres »… quand l'entreprise a plusieurs contrats. */
    populationLibelle: facultatif(80),
    /** S1428 — informatif seulement : aucune règle d'éligibilité ne le lit. Absent : « Non renseigné ». */
    populationCouverte: z.enum(POPULATIONS_COUVERTES).optional().nullable(),
    /** La précision d'« Autre », et de lui seul. */
    populationPrecision: facultatif(PRECISION_POPULATION_MAX),
    /** « À compter du ». */
    dateDebut: dateCivile,
    /** « Durée … an(s) », convertie en mois par l'écran. */
    dureeMois: z.number().int().min(1).max(600).optional().nullable(),
    effectifDeclare: z.number().int().min(0).optional().nullable(),
    effectifFemmes: z.number().int().min(0).optional().nullable(),
    effectifHommes: z.number().int().min(0).optional().nullable(),
    masseSalarialeAnnuelle: montant.optional().nullable(),
    cotisationMensuelle: montant.optional().nullable(),
    tauxParAdherent: z.number().min(0).max(100).optional().nullable(),
    cotisationAnnuelle: montant.optional().nullable(),
    tauxPartPatronale: pourcentage.optional().nullable(),
    tauxPartSalariale: pourcentage.optional().nullable(),
    lieuSignature: facultatif(120),
    dateSignature: dateCivile.optional().nullable(),
});
/**
 * Ce que la fiche de souscription doit respecter, au-delà du format : la
 * MÊME règle pour l'écran et pour l'API, avec le même motif — l'écran
 * refuse sans aller-retour, et le serveur refuse ce que l'écran a laissé
 * passer.
 */
export function validerSouscription(s) {
    const population = refusPopulationCouverte(s.populationCouverte, s.populationPrecision);
    if (population !== null)
        return { motif: population };
    if (s.tauxPartPatronale !== null &&
        s.tauxPartPatronale !== undefined &&
        s.tauxPartSalariale !== null &&
        s.tauxPartSalariale !== undefined &&
        Math.abs(s.tauxPartPatronale + s.tauxPartSalariale - 100) > 0.01) {
        return { motif: 'Les parts patronale et salariale doivent faire 100 % à elles deux.' };
    }
    const f = s.effectifFemmes ?? null;
    const h = s.effectifHommes ?? null;
    if (f !== null && h !== null && s.effectifDeclare != null && f + h !== s.effectifDeclare) {
        return { motif: "L'effectif à affilier doit être la somme des femmes et des hommes déclarés." };
    }
    return null;
}
export const schemaCreationEntreprise = schemaIdentification
    .extend({
    /** Code souscripteur AFS. Absent : le prochain numéro libre. */
    code: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,19}$/, 'Code : lettres, chiffres, tirets.')
        .optional(),
    /**
     * CP-07 — obligatoire : une entreprise FUNTOA créée sous AFS par défaut
     * compterait ses affiliés chez AFS sans que personne le voie.
     */
    entiteAffiliationId: z.uuid({ error: "Choisissez l'entité d'affiliation." }),
    contrat: schemaSouscription,
})
    .strict();
export const schemaModificationEntreprise = schemaIdentification
    .partial()
    .extend({
    entiteAffiliationId: z.uuid().optional(),
    /** Décision 338 — l'âge jusqu'auquel les enfants des salariés sont couverts. */
    ageLimiteEnfant: z
        .number()
        .int()
        .min(AGE_LIMITE_ENFANT_MIN, `Âge limite des enfants : de ${String(AGE_LIMITE_ENFANT_MIN)} à ${String(AGE_LIMITE_ENFANT_MAX)} ans.`)
        .max(AGE_LIMITE_ENFANT_MAX, `Âge limite des enfants : de ${String(AGE_LIMITE_ENFANT_MIN)} à ${String(AGE_LIMITE_ENFANT_MAX)} ans.`)
        .optional(),
})
    .strict();
/**
 * Décision 369 — retirer un « autre nom connu » d'une entreprise : l'accueil
 * ne la trouve plus par ce libellé. Le libellé se compare sans casse ni accents.
 */
export const schemaRetraitAlias = z
    .object({ libelle: z.string().trim().min(1, 'Le nom à retirer est vide.').max(200) })
    .strict();
export const schemaContratDetaille = z.object({
    id: z.uuid(),
    offre: z.object({ id: z.uuid(), code: z.string(), nom: z.string() }),
    populationLibelle: z.string().nullable(),
    /** S1428 — `null` : « Non renseigné » (les contrats d'avant). */
    populationCouverte: z.enum(POPULATIONS_COUVERTES).nullable(),
    populationPrecision: z.string().nullable(),
    dateDebut: z.string(),
    dateFin: z.string().nullable(),
    actif: z.boolean(),
    dureeMois: z.number().int().nullable(),
    effectifDeclare: z.number().int().nullable(),
    effectifFemmes: z.number().int().nullable(),
    effectifHommes: z.number().int().nullable(),
    masseSalarialeAnnuelle: z.number().nullable(),
    cotisationMensuelle: z.number().nullable(),
    tauxParAdherent: z.number().nullable(),
    cotisationAnnuelle: z.number().nullable(),
    tauxPartPatronale: z.number().nullable(),
    tauxPartSalariale: z.number().nullable(),
    lieuSignature: z.string().nullable(),
    dateSignature: z.string().nullable(),
    /** Titulaires actifs rattachés à ce contrat. */
    salariesActifs: z.number().int(),
    /** Décision 374 (c) — le jour et le motif de la clôture ; `null` tant qu'il est actif. */
    dateCloture: z.string().nullable(),
    motifCloture: z.string().nullable(),
    /** Les fiches non supprimées qui l'ont pour contrat, actives ou non : elles empêchent la clôture. */
    personnesRattachees: z.number().int(),
    /** Toutes les fiches qui l'ont porté, supprimées comprises : elles empêchent la suppression. */
    fichesRattachees: z.number().int(),
    /** Décision 381 (3) : des fiches l'ont quitté depuis (le journal le dit) ; elles empêchent aussi la suppression. */
    fichesParties: z.boolean(),
    /** Tous les imports DNS déposés sur lui. */
    importsDns: z.number().int(),
    /** Un DNS de ce contrat qui n'est ni appliqué, ni rejeté, ni annulé. */
    dnsEnCours: z.boolean(),
    /** S1427 — le scan du contrat signé, s'il est joint : de quoi l'afficher, jamais le fichier. */
    scan: schemaFichierPiece.nullable(),
});
/**
 * S1427 — le scan d'un contrat : PDF, JPEG ou PNG (lus dans les octets), au
 * plus 10 Mo. Le même plafond que la pièce d'un ayant droit : un contrat de
 * plusieurs pages scanné en PDF doit passer, et rester lisible.
 */
export const TAILLE_MAX_SCAN_CONTRAT_OCTETS = 10 * 1024 * 1024;
/** R5 — la base désigne un fichier que le disque n'a plus. */
export const MESSAGE_SCAN_NON_CONSERVE = 'Fichier non conservé.';
/** Les clés viennent de l'écriture : un champ ajouté là ne manque pas ici. */
function enLecture(forme) {
    // `Object.fromEntries` perd le type des clés ; elles sont celles de `forme`.
    return Object.fromEntries(Object.keys(forme).map((cle) => [cle, z.string().nullable()]));
}
const schemaIdentificationLue = z
    .object(enLecture(schemaIdentification.shape))
    .extend({ nom: z.string() });
// Lue, chaque valeur est là : `null` quand elle manque, jamais absente.
export const schemaEntrepriseDetaillee = schemaIdentificationLue.extend({
    id: z.uuid(),
    code: z.string(),
    entiteAffiliation: schemaEntiteAffiliation,
    statut: z.enum(STATUTS_ADHERENT),
    dateSuspension: z.string().nullable(),
    dateResiliation: z.string().nullable(),
    dateFinPreavis: z.string().nullable(),
    /** Le motif du dernier changement de statut ; `null` tant que rien ne l'a changé. */
    motifStatut: z.string().nullable(),
    /** Décision 338 — enfants couverts jusqu'à cet âge, jour anniversaire inclus (21 par défaut). */
    ageLimiteEnfant: z.number().int(),
    aConfirmer: z.boolean(),
    motifAConfirmer: z.string().nullable(),
    dernierDnsRecuLe: z.string().nullable(),
    joursSansDns: z.number().int().nullable(),
    dnsObsolete: z.boolean(),
    /** Le dernier DNS reçu qui n'est ni appliqué, ni rejeté, ni annulé. */
    dnsEnAttente: z
        .object({ id: z.uuid(), statut: z.enum(STATUTS_IMPORT_DNS), recuLe: z.string() })
        .nullable(),
    /** Le plus ancien `dateDebut` des contrats : « adhérente depuis ». */
    adherenteDepuis: z.string().nullable(),
    alias: z.array(z.string()),
    contrats: z.array(schemaContratDetaille),
    salariesActifs: z.number().int(),
    salariesInactifs: z.number().int(),
    nbImportsDns: z.number().int(),
    creeLe: z.string(),
});
// ── Cycle de vie de l'adhésion (rules/adhesion.ts) ─────────────────────────
/** Pourquoi : lu par la Direction et par la chargée, jamais une donnée de salarié. */
const motifDeStatut = z
    .string()
    .trim()
    .min(5, 'Le motif est obligatoire (5 caractères au moins)')
    .max(500);
/** Suspendre, lever une suspension, annuler une résiliation : un motif, rien d'autre. */
export const schemaGesteAdhesion = z.object({ motif: motifDeStatut }).strict();
/**
 * CP-05 — la résiliation ouvre un préavis. Sans dates, la résiliation est du
 * jour et le préavis court trois mois ; les deux se corrigent, parce que le
 * point de départ du préavis (courrier, réception, fin de mois) reste à
 * trancher par AFS.
 */
export const schemaResiliation = z
    .object({
    motif: motifDeStatut,
    dateResiliation: dateCivile.optional(),
    dateFinPreavis: dateCivile.optional(),
})
    .strict();
export const schemaCodeSuivant = z.object({ code: z.string() });
/** Le catalogue des offres proposées — ce que la fiche de souscription fait choisir. */
export const schemaOffreCatalogue = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
});
// ─────────────────────────────────────────────────────────────────────
//  Salariés d'une entreprise
// ─────────────────────────────────────────────────────────────────────
export const schemaFiltresSalaries = schemaPagination.extend({
    /** Matricule exact ou fragment de nom. */
    recherche: z.string().trim().max(120).optional(),
    statut: z.enum(STATUTS_PERSONNE).optional(),
});
export const schemaSalarieListe = z.object({
    id: z.uuid(),
    matricule: z.string().nullable(),
    nom: z.string(),
    prenom: z.string().nullable(),
    fonction: z.string().nullable(),
    statut: z.enum(STATUTS_PERSONNE),
    motifInactivite: z.string().nullable(),
    inactifDepuis: z.string().nullable(),
    dateEntree: z.string().nullable(),
    dateDepart: z.string().nullable(),
    etatConfirmation: z.enum(ETATS_CONFIRMATION),
    contrat: z.object({
        id: z.uuid(),
        populationLibelle: z.string().nullable(),
        offre: schemaOffreCatalogue,
    }),
    nbAyantsDroit: z.number().int(),
});
export const schemaPageSalaries = schemaPage(schemaSalarieListe);
/** Changer l'offre d'un contrat, pour tous ses salariés (écart d'offre, R12). */
export const schemaChangementOffreContrat = z.object({ offreId: z.uuid() }).strict();
//# sourceMappingURL=entreprises.js.map